import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'dezoom_pwa_install_dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
}

function isDismissed(): boolean {
  return localStorage.getItem(DISMISS_KEY) === '1';
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [delayElapsed, setDelayElapsed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return undefined;

    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', onBip);
    const timer = window.setTimeout(() => setDelayElapsed(true), 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!delayElapsed || isStandalone() || isDismissed()) return;
    if (deferredPrompt) {
      setVisible(true);
    } else if (isIosSafari()) {
      setIosHint(true);
      setVisible(true);
    }
  }, [delayElapsed, deferredPrompt]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
    return choice.outcome === 'accepted';
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
    setIosHint(false);
  }, []);

  return {
    visible,
    canInstall: !!deferredPrompt,
    iosHint,
    install,
    dismiss,
  };
}
